import { ReactElement, useState } from 'react';
import { Component as CaretDownIcon } from 'icons/icon-ui--caret-outline.svg';
import { Component as CaretUpIcon } from 'icons/icon-ui--caret-up-outline.svg';
import { Component as ShieldCheckIcon } from 'icons/icon--shield-check.svg';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import { useAddress } from '../../contexts/AddressContext';
import styles from './OrderConfirmation.module.scss';

function SummaryRow({
  label,
  value,
}: { label: string; value: string }) {
  return (
    <div className={styles.receipt_row}>
      <span>{label}</span>
      <div className={styles.receipt_row_hr} />
      <span>{value}</span>
    </div>
  );
}

function parseCurrency(str: string): number {
  return parseFloat(str.replace(/[$,]/g, '')) || 0;
}

export function OrderReceipt(): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen((prev) => !prev);
  const { cart } = useMenuWithCart();
  const { addresses } = useAddress();
  const shippingAddress = addresses.find((a) => a.id === cart?.shippingAddressId);

  const mealSummaries = (cart?.mealsSummaries ?? []).filter((s) => s.count > 0);

  return (
    <div className={styles.order_receipt}>
      <div
        role="button"
        tabIndex={0}
        className={styles.order_receipt_header}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <h3 className={styles.order_receipt_title}>Order Receipt</h3>
        {isOpen ? <CaretUpIcon width={23} /> : <CaretDownIcon width={23} />}
      </div>
      <hr className={styles.divider} />
      {isOpen && cart && (
        <div className={styles.order_receipt_body}>
          <div className={styles.receipt_rows}>
            {mealSummaries.map(({
              name,
              count,
              price,
            }) => (
              <SummaryRow
                key={name}
                label={`${count} ${name}`}
                value={`$${price.toFixed(2)}`}
              />
            ))}
            <SummaryRow label="Sales Tax" value={cart.priceSummary.tax} />
            <SummaryRow label="Shipping" value={cart.priceSummary.shipping} />
            <SummaryRow
              label="Extended Area Surcharge"
              value={cart.priceSummary.extendedAreaSurcharge || '$0.00'}
            />
            {parseCurrency(cart.priceSummary.totalDiscountAmount) > 0 && (
              <SummaryRow
                label="Discount"
                value={`-${cart.priceSummary.totalDiscountAmount}`}
              />
            )}
            <div className={styles.receipt_row_total}>
              <strong>Total</strong>
              <strong>{cart.priceSummary.total}</strong>
            </div>
          </div>
          <div className={styles.receipt_recipient}>
            <p className={styles.receipt_recipient_label}>Recipient</p>
            {(shippingAddress?.first_name || shippingAddress?.last_name) && (
              <p className={styles.receipt_recipient_name}>
                {[shippingAddress.first_name, shippingAddress.last_name].filter(Boolean)
                  .join(' ')}
              </p>
            )}
            {shippingAddress?.local_phone_number && (
              <p className={styles.receipt_recipient_phone}>
                {shippingAddress.local_phone_number}
              </p>
            )}
            <hr className={styles.receipt_recipient_divider} />
            <p className={styles.receipt_recipient_label}>Delivery Address</p>
            {shippingAddress?.address && (
              <p className={styles.receipt_recipient_address_line}>{shippingAddress.address}</p>
            )}
            {shippingAddress?.address2 && (
              <p className={styles.receipt_recipient_address_line}>{shippingAddress.address2}</p>
            )}
            {(shippingAddress?.city || shippingAddress?.state || shippingAddress?.zip) && (
              <p className={styles.receipt_recipient_address_line}>
                {[shippingAddress.city, shippingAddress.state].filter(Boolean)
                  .join(', ')}
                {shippingAddress.zip ? ` ${shippingAddress.zip}` : ''}
              </p>
            )}
          </div>
          <div className={styles.receipt_tip_card}>
            <div className={styles.receipt_tip_content}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ShieldCheckIcon />
                <p className={styles.receipt_tip_title} style={{ margin: 0 }}>Our Guarantee</p>
              </div>
              <p className={styles.receipt_tip_body}>
                If your order arrives late, damaged, or spoiled: we will credit your account.
              </p>
            </div>
            <a href="/pages/pc-guarantee" className={styles.receipt_tip_button}>
              Learn How it
              Works
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
